/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import type { ConnectionSummary } from '../../api/areas/integrations';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useApi } from '../../lib/useApi';
import { StatusBadge } from './shared';

/** Settings › Connections › OT&E tests: every EPP connection, each opening its own tests tab on OT&E. */
export function OteTestsPanel() {
  const { t } = useTranslation('nav');
  const result = useApi((signal) => api.listConnections({ protocol: 'EPP' }, { signal }), []);
  const columns: Column<ConnectionSummary>[] = [
    {
      key: 'name',
      header: t('ui.ote.connection'),
      cell: (c) => (
        <Link to={`/settings/registries/${encodeURIComponent(c.code)}/tests`} className="font-medium text-primary hover:underline">
          {c.name}
        </Link>
      ),
    },
    { key: 'status', header: t('ui.ote.status'), cell: (c) => <StatusBadge group="connectionStatus" value={c.status} /> },
    {
      key: 'open',
      header: '',
      className: 'text-right',
      cell: (c) => (
        <Button asChild variant="outline" size="sm">
          <Link to={`/settings/registries/${encodeURIComponent(c.code)}/tests`}>
            {t('ui.ote.open')} <ArrowRight />
          </Link>
        </Button>
      ),
    },
  ];
  return (
    <>
      <PageHeader title={t('ui.ote.title')} subtitle={t('ui.ote.subtitle')} />
      {result.status === 'loading' ? <Loading rows={3} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
      {result.status === 'success' ? (
        <Section flush>
          <DataTable columns={columns} rows={result.data.items} rowKey={(c) => c.id} empty={<EmptyState title={t('ui.ote.none')} />} />
        </Section>
      ) : null}
    </>
  );
}
